import Category from "@/components/category";
import LinkedContent from "@/components/linked-content";
import TeaserByLineImage from "@/components/teaser-byline-image";
import TeaserDownpull from "@/components/teaser-downpull";
import TeaserHeading from "@/components/teaser-heading";
import Vinjett from "@/components/vinjett";
import { Content } from "@/services/content/types";
import { createImageSizes, createImageUrl } from "@/utils/image";
import { wrapPrefix } from "@/utils/teaser";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";

type LargeProps = {
  article: Content.Article;
  showLinkedContent?: boolean;
  largeHeader?: boolean;
  bigLargeArticle?: boolean;
  showLeftLine?: boolean;
  imagePriority?: boolean;
  hideThumbnailImage?: boolean;
};

const Large = (props: LargeProps) => {
  const category = props.article.fields.categories?.[0];
  const image =
    props.article.fields.teaserImage || props.article.fields.images?.[0];
  const title = props.article.fields.teaserTitle || props.article.fields.title;
  const author = props.article.fields.authors?.[0];
  const excerpt = props.article.fields.excerpt
    ? wrapPrefix(/^(Liverapport:?)/i, props.article.fields.excerpt, "font-bold")
    : null;
  const showByline =
    props.article.fields.showByline !== undefined
      ? props.article.fields.showByline
      : false;
  const bylineImage =
    props.article.fields.hideBylineImage !== undefined
      ? props.article.fields.hideBylineImage
      : false;

  const hasLinkedContent = Boolean(props.article.fields.linkedContent?.length);
  const highlighted = props?.article?.fields?.highlighted_v2;
  const parentCategory = category?.fields.parentCategory;
  const color = category?.fields.color || parentCategory?.fields.color;
  const tipUs = props.article.fields.tipUs;
  const vinjett = props.article.fields.vinjett;
  const teaserLayoutType = props.article.fields.teaserLayoutType;

  return (
    <LinkedContent
      linkedContent={props.article.fields.linkedContent}
      color={color}
      highlighted={highlighted}
      showLinkedContent={props.showLinkedContent}
      bigLargeArticle={props.bigLargeArticle}
      showLeftLine={props.showLeftLine}
      compactMode={false}
      category={category}
      teaserLayoutType={teaserLayoutType}
      forceBoxed={true}
    >
      <Link
        href={`/${props.article.fields.slug}`}
        className={classNames(
          "group block relative pb-4 border-b border-sand-800",
          {
            "text-center !border-red-950 text-static-white":
              highlighted === "Nivå 1(röd)",
            "text-center bg-sand-700 !border-sand-900":
              highlighted === "Nivå 2(beige)",
            "bg-red-900": highlighted === "Nivå 1(röd)",
            "": bylineImage,
            "!border-b-0 !pb-0": teaserLayoutType == "Rubrik på bild",
            "border-b border-sand-800":
              teaserLayoutType === "Centrerad rubrik" && hasLinkedContent,
            "!border-b-0 pb-0": !hasLinkedContent && highlighted,
          },
        )}
      >
        {vinjett && <Vinjett content={vinjett} />}

        {teaserLayoutType == "Rubrik på bild" &&
        image?.fields?.file?.url &&
        !props.hideThumbnailImage ? (
          <div
            className={classNames("relative overflow-hidden", {
              "-ml-3 -mr-4": !!highlighted,
            })}
          >
            <div className="aspect-w-16 aspect-h-10">
              <Image
                priority={props.imagePriority ? true : false}
                fill
                src={createImageUrl(image.fields.file.url, { crop: "16:10" })}
                alt={image.fields.alt || ""}
                className="object-cover"
                sizes={createImageSizes({
                  mobile: "100vw",
                  tablet: "100vw",
                  desktop: "50vw",
                })}
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-2 md:p-6 pb-2 md:pb-6 bg-gradient-to-t from-black/90 to-transparent text-static-white">
              {category && !highlighted && <Category category={category} />}
              {title && (
                <TeaserHeading
                  text={title}
                  highlighted={highlighted}
                  className={classNames(
                    "font-semibold text-static-white",
                    props.largeHeader ? "text-3xl md:text-5xl" : "text-3xl",
                    !!highlighted && "!text-3xl md:!text-6xl font-bold",
                  )}
                />
              )}
              {excerpt && (
                <span className="block mt-2 text-sm md:text-base text-static-white">
                  {showByline && (
                    <span className="font-bold">{author?.fields.name}: </span>
                  )}
                  <span>{props.article.fields.excerpt}</span>
                </span>
              )}
            </div>
          </div>
        ) : (
          <>
            {image?.fields?.file?.url && !props.hideThumbnailImage && (
              <div
                className={classNames("aspect-w-16 aspect-h-9 mb-4", {
                  "-ml-[12px] -mr-4 mb-6": !!highlighted,
                })}
              >
                <Image
                  priority={props.imagePriority ? true : false}
                  fill
                  src={createImageUrl(image.fields.file.url, { crop: "16:9" })}
                  alt={image.fields.alt || ""}
                  className="object-cover"
                  sizes={createImageSizes({
                    mobile: "100vw",
                    tablet: "100vw",
                    desktop: "50vw",
                  })}
                />
              </div>
            )}
            <div
              className={classNames({
                "px-4": !!highlighted && !hasLinkedContent,
              })}
            >
              {category && !highlighted && (
                <Category
                  category={category}
                  centered={teaserLayoutType === "Centrerad rubrik"}
                />
              )}

              {title && (
                <TeaserHeading
                  text={title}
                  highlighted={highlighted}
                  className={classNames(
                    "font-semibold",
                    bylineImage ? "max-w-[76%] md:max-w-[70%]" : "",
                    teaserLayoutType === "Centrerad rubrik"
                      ? "text-center"
                      : "",
                    props.largeHeader ? "text-3xl md:text-5xl" : "text-3xl",
                    !!highlighted && "!text-5xl md:!text-6xl font-bold",
                  )}
                />
              )}
              <div
                className={classNames(
                  bylineImage ? "max-w-[75%] md:max-w-[70%]" : "",
                )}
              >
                <TeaserDownpull
                  highlighted={highlighted}
                  showByline={showByline}
                  authorName={author?.fields.name}
                  excerpt={props.article.fields.excerpt}
                  showbyLineImage={bylineImage}
                  centered={teaserLayoutType === "Centrerad rubrik"}
                />
              </div>

              {bylineImage && author?.fields.image?.fields?.file?.url && (
                <TeaserByLineImage
                  showbyLineImage={bylineImage}
                  fileUrl={author?.fields.image?.fields?.file?.url}
                  altText={
                    author?.fields.name || author?.fields?.image?.fields?.title
                  }
                  width={"190px"}
                  height={"190px"}
                  wrapperClassNames={`max-w-[25%] md:max-w-[30%] ${
                    !hasLinkedContent && bylineImage ? "" : ""
                  }`}
                />
              )}
            </div>
          </>
        )}
      </Link>
      {tipUs && (
        <span className="block my-4 bg-sand-600 p-4 rounded-sm">
          <strong>Vet du mer?</strong> Tipsa oss på{" "}
          <a href="mailto:tipsa@efn.se" className="text-blue-link">
            tipsa@efn.se
          </a>{" "}
          eller{" "}
          <a href="tel:+4687259000" className="text-blue-link">
            08-725 90 00
          </a>
        </span>
      )}
    </LinkedContent>
  );
};

export default Large;
